import {
  PLAN_HIERARCHY,
  FEATURE_CATEGORIES,
  type FeatureCategory,
} from '@hapana/shared/constants/entitlements.constants'
import { planTierSchema, type PlanTier } from '@hapana/shared/schemas/entitlements/plan-tier.schema'

/**
 * Whether customers may manage their own plan from Core (OC-320).
 *
 * OFF, deliberately. Plans are set by Hapana staff in Admin this pass — "no self service to
 * customers" (Lakshmi, 24 Jul); proper upgrade/downgrade paths are roadmapped for October. Flipping
 * this to `true` is the whole switch: it enables the Control Centre card and opens the route.
 *
 * A code constant rather than a permission bit on purpose. `createDefaultAdminPolicy` builds "All
 * Access" from `Object.values(CorporatePermissions)`, so the reserved `manage_plan` bit lands
 * automatically in every NEWLY provisioned brand's policy (existing brands predate it — the same
 * asymmetry migration 000205 had to backfill for bits 135/136). A permission bit therefore cannot
 * hold this closed; a constant can.
 *
 * Backend gating is separate and still applies: the `product-tiering` PostHog flag keeps the
 * entitlement engine inert regardless of this value.
 */
export const IS_PLAN_SELF_SERVICE_ENABLED = false

/**
 * Which plan to SHOW for a brand that has none assigned.
 *
 * Mirrors UNASSIGNED_TIER in EntitlementService — if the backend treats un-tiered as premium, the
 * UI must show premium, or the page contradicts what the customer actually gets.
 */
export const UNASSIGNED_PLAN_FALLBACK: PlanTier = 'premium'

/** Tiers in ascending order, derived from the schema so a new tier appears without editing this. */
export const PLAN_TIERS_ASCENDING: readonly PlanTier[] = [...planTierSchema.options].sort(
  (a, b) => PLAN_HIERARCHY[a] - PLAN_HIERARCHY[b]
)

/**
 * Categories the comparison table renders, in catalogue order.
 *
 * Derived from the shared FEATURE_CATEGORIES catalogue rather than a hand-maintained list, so a
 * feature moved between categories updates the table on its own.
 */
export const COMPARISON_CATEGORIES = Object.keys(FEATURE_CATEGORIES) as FeatureCategory[]

/** Resolves a value read off the untyped corporate store into a tier the UI can render. */
export function resolvePlanTier(rawPlanTier: unknown): PlanTier {
  const parsed = planTierSchema.safeParse(rawPlanTier)
  return parsed.success ? parsed.data : UNASSIGNED_PLAN_FALLBACK
}

/** The tier immediately below the given one, used for the "Everything in X plus:" line. */
export function getPreviousTier(tier: PlanTier): PlanTier | null {
  const index = PLAN_TIERS_ASCENDING.indexOf(tier)
  return index > 0 ? (PLAN_TIERS_ASCENDING[index - 1] ?? null) : null
}
